// ============================================================================
// WORK ITEM TREE COMPONENT
// Recursive tree view for hierarchical work items with drag-and-drop,
// accessibility (ARIA tree roles + keyboard navigation), style presets,
// template-aware badges, assignee avatars, SLA indicators, and context menus.
// Date: October 25, 2025
// Updated: February 2026 — Major enhancement pass
// ============================================================================

'use client';

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useResponsive } from '@/hooks/useResponsive';
import {
  ChevronRight,
  ChevronDown,
  Circle,
  CheckCircle,
  Clock,
  AlertCircle,
  Plus,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Copy,
  FolderTree,
  User,
  Settings2
} from 'lucide-react';
import type { WorkItem } from './UniversalWorkItems';
import type { WorkItemTemplate } from './WorkItemModal';
import { getKanbanStylePreset } from '@/components/kanban/KanbanStylePresets';
import { getKBAvatarColor, getKBInitials } from '@/components/kanban/kanban-shared';
import QuickAddTaskButton from './shared/QuickAddTaskButton';
import SLATimer from './shared/SLATimer';
import WorkItemHoverCard from '@/components/work-items/WorkItemHoverCard';
import { copyTextToClipboard } from '@/lib/clipboard';
import { getWorkItemTypeAccent, getWorkItemTypeIdentity } from '@/lib/work-items/typeIdentity';
import {
  getWorkItemsAccentPanelStyle,
  WorkItemsTypeAccentBadge,
} from '@/components/work-items/WorkItemsViewAccent';
import { isTerminalWorkItemStatus, normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';
import { getStandardWorkItemStatusBadgeClass, formatAppointmentDisplayStatusLabel } from '@/lib/appointmentStatusVisual';

// ============================================================================
// TYPES
// ============================================================================

/** Tree-specific extension: items carry their resolved children array */
export interface TreeWorkItem extends WorkItem {
  children?: TreeWorkItem[];
  depth?: number;
}

export type TreeVisibleColumnKey = 'type' | 'assignees' | 'progress' | 'status' | 'actions';

export interface WorkItemTreeProps {
  items: TreeWorkItem[];
  onViewItem?: (item: TreeWorkItem) => void;
  /** @deprecated Use onViewItem — kept for backward compat */
  onItemClick?: (item: TreeWorkItem) => void;
  onAddChild?: (parentItem: TreeWorkItem) => void;
  onEditItem?: (item: TreeWorkItem) => void;
  onDeleteItem?: (itemId: string) => void;
  onMoveItem?: (itemId: string, newParentId: string | null) => void;
  /** Callback when a team member avatar is clicked to open their profile (March 2026) */
  onOpenTeamMember?: (userId: number) => void;
  maxDepth?: number;
  showProgress?: boolean;
  showMetrics?: boolean;
  showAssignees?: boolean;
  showContact?: boolean;
  showSLA?: boolean;
  showType?: boolean;
  showStatus?: boolean;
  showActions?: boolean;
  loading?: boolean;
  enableDragDrop?: boolean;
  templates?: WorkItemTemplate[];
  stylePreset?: string;
  className?: string;
  activeItemType?: string | null;
  activeItemTypeLabel?: string;
  primaryColor?: string;
  headerActions?: React.ReactNode;
  onOpenBoardPreferences?: () => void;
}

interface TreeNodeProps {
  item: TreeWorkItem;
  depth: number;
  maxDepth: number;
  onViewItem?: (item: TreeWorkItem) => void;
  onAddChild?: (parentItem: TreeWorkItem) => void;
  onEditItem?: (item: TreeWorkItem) => void;
  onDeleteItem?: (itemId: string) => void;
  onOpenTeamMember?: (userId: number) => void;
  showProgress?: boolean;
  showMetrics?: boolean;
  showAssignees?: boolean;
  showContact?: boolean;
  showSLA?: boolean;
  showType?: boolean;
  showStatus?: boolean;
  showActions?: boolean;
  expandedMap: Map<string, boolean>;
  onToggleExpand: (itemId: string) => void;
  isMobile?: boolean;
  templates?: WorkItemTemplate[];
  focusedId: string | null;
  onFocusChange: (id: string | null) => void;
  onNavigate: (direction: 'up' | 'down' | 'left' | 'right', currentId: string) => void;
  flatIndex: number;
  primaryColor?: string;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getStatusIcon = (status?: string, itemType?: string | null) => {
  const k = normalizeWorkflowStatusKey(status);
  if (isTerminalWorkItemStatus(status, itemType)) {
    return <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />;
  }
  if (k.includes('progress') || k === 'active') {
    return <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
  }
  if (k.includes('blocked') || k === 'overdue') {
    return <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
  }
  return <Circle className="h-4 w-4 text-gray-400 dark:text-gray-500" />;
};

const getStatusColor = (status?: string, itemType?: string | null): string => {
  return getStandardWorkItemStatusBadgeClass(status || '', itemType || undefined);
};

const getPriorityColor = (priority?: string): string => {
  if (!priority) return 'bg-gray-200 dark:bg-gray-600';
  const priorityLower = priority.toLowerCase();
  if (priorityLower === 'urgent' || priorityLower === 'critical') return 'bg-red-500';
  if (priorityLower === 'high') return 'bg-orange-500';
  if (priorityLower === 'medium') return 'bg-yellow-500';
  return 'bg-green-500';
};

/** Resolve template for an item, using item_subtype or item_type */
const getTemplate = (item: TreeWorkItem, templates?: WorkItemTemplate[]): WorkItemTemplate | null => {
  if (!templates || templates.length === 0) return null;
  if (item.item_subtype) {
    const bySubtype = templates.find(t => t.applies_to_subtype === item.item_subtype || t.item_subtype === item.item_subtype);
    if (bySubtype) return bySubtype;
  }
  return templates.find(t => t.applies_to_item_type === item.item_type) || null;
};

/** Get badge color from template or fallback to default purple */
const getTypeBadgeStyle = (item: TreeWorkItem, templates?: WorkItemTemplate[], fallbackColor = '#3b82f6'): { bg: string; text: string; border: string } => {
  const template = getTemplate(item, templates);
  if (template?.color) {
    return { bg: template.color, text: 'white', border: template.color };
  }
  const identity = getWorkItemTypeIdentity(item.item_type, fallbackColor);
  const accent = getWorkItemTypeAccent(item.item_type, fallbackColor);
  return { bg: accent.tintStrong, text: identity.color, border: accent.border };
};

// ============================================================================
// CONTEXT MENU COMPONENT
// ============================================================================

interface ContextMenuProps {
  item: TreeWorkItem;
  onViewItem?: (item: TreeWorkItem) => void;
  onEditItem?: (item: TreeWorkItem) => void;
  onDeleteItem?: (itemId: string) => void;
  onAddChild?: (item: TreeWorkItem) => void;
  onClose: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  item,
  onViewItem,
  onEditItem,
  onDeleteItem,
  onAddChild,
  onClose
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const menuItems = [
    onViewItem && { label: 'View details', icon: Eye, action: () => { onViewItem(item); onClose(); } },
    onEditItem && { label: 'Edit', icon: Edit, action: () => { onEditItem(item); onClose(); } },
    onAddChild && { label: 'Add child item', icon: Plus, action: () => { onAddChild(item); onClose(); } },
    { label: 'Copy ID', icon: Copy, action: () => { void copyTextToClipboard(item.item_number || item.id).catch((error) => console.error('Failed to copy work item ID:', error)).finally(onClose); } },
    onDeleteItem && { label: 'Delete', icon: Trash2, action: () => { onDeleteItem(item.id); onClose(); }, danger: true },
  ].filter(Boolean) as { label: string; icon: React.ElementType; action: () => void; danger?: boolean }[];

  return (
    <div
      ref={menuRef}
      className="absolute right-0 top-full mt-1 z-50 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 animate-in fade-in slide-in-from-top-1 duration-150"
    >
      {menuItems.map((mi, idx) => (
        <button
          key={idx}
          onClick={mi.action}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
            mi.danger
              ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <mi.icon className="h-4 w-4" />
          {mi.label}
        </button>
      ))}
    </div>
  );
};

// ============================================================================
// ASSIGNEE AVATAR COMPONENT
// ============================================================================

const AssigneeAvatars: React.FC<{ item: TreeWorkItem; onOpenTeamMember?: (userId: number) => void; maxVisible?: number }> = ({ item, onOpenTeamMember, maxVisible = 3 }) => {
  const assignees = item.assignees;
  if (!assignees || assignees.length === 0) return null;

  const visibleAssignees = assignees.slice(0, maxVisible);
  const remaining = assignees.length - maxVisible;

  return (
    <div className="flex items-center -space-x-1.5">
      {visibleAssignees.map((a) => (
        <button
          key={a.user_id}
          type="button"
          onClick={(e) => { e.stopPropagation(); if (a.user_id && onOpenTeamMember) onOpenTeamMember(a.user_id); }}
          className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium text-white ring-2 ring-white dark:ring-gray-800 ${getKBAvatarColor(a.email, a.first_name)} ${onOpenTeamMember ? 'cursor-pointer hover:ring-orange-400 hover:scale-110 transition-transform' : ''}`}
          title={`${a.first_name} ${a.last_name}`}
        >
          {getKBInitials(a.first_name, a.last_name, a.email)}
        </button>
      ))}
      {remaining > 0 && (
        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 ring-2 ring-white dark:ring-gray-800">
          +{remaining}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SKELETON LOADING COMPONENT
// ============================================================================

const TreeSkeleton: React.FC = () => {
  const rows = [0, 1, 2, 1, 2, 3, 0, 1, 0];
  return (
    <div className="space-y-1 animate-pulse" role="presentation">
      {rows.map((depth, idx) => (
        <div
          key={idx}
          className="flex items-center gap-2 py-2.5 px-3 rounded-lg"
          style={{ paddingLeft: `${depth * 24 + 12}px` }}
        >
          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded-full" />
          <div className="w-16 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded max-w-xs" />
          <div className="w-12 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full" />
          <div className="w-16 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// TREE NODE COMPONENT
// ============================================================================

const TreeNode: React.FC<TreeNodeProps> = ({
  item,
  depth,
  maxDepth,
  onViewItem,
  onAddChild,
  onEditItem,
  onDeleteItem,
  onOpenTeamMember,
  showProgress = true,
  showMetrics = true,
  showAssignees = true,
  showContact = false,
  showSLA = false,
  showType = true,
  showStatus = true,
  showActions = true,
  expandedMap,
  onToggleExpand,
  isMobile = false,
  templates,
  focusedId,
  onFocusChange,
  onNavigate,
  flatIndex,
  primaryColor = '#3b82f6'
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const canExpand = hasChildren && depth < maxDepth;
  const isExpanded = expandedMap.get(item.id) !== false;
  const indentWidth = depth * (isMobile ? 16 : 24);
  const isFocused = focusedId === item.id;
  const rowRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Focus management
  useEffect(() => {
    if (isFocused && rowRef.current) {
      rowRef.current.focus({ preventScroll: false });
    }
  }, [isFocused]);

  // Keyboard handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        onNavigate('down', item.id);
        break;
      case 'ArrowUp':
        e.preventDefault();
        onNavigate('up', item.id);
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (canExpand && !isExpanded) {
          onToggleExpand(item.id);
          const firstChildId = item.children?.[0]?.id;
          if (firstChildId) {
            if (typeof window !== 'undefined') {
              window.requestAnimationFrame(() => onFocusChange(firstChildId));
            } else {
              onFocusChange(firstChildId);
            }
          }
        } else if (canExpand && isExpanded) {
          const firstChildId = item.children?.[0]?.id;
          if (firstChildId) {
            onFocusChange(firstChildId);
          } else {
            onNavigate('down', item.id);
          }
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (isExpanded && canExpand) {
          onToggleExpand(item.id); // Collapse
        } else {
          onNavigate('left', item.id); // Move to parent
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onViewItem?.(item);
        break;
    }
  };

  // Template-aware badge
  const template = getTemplate(item, templates);
  const badgeStyle = getTypeBadgeStyle(item, templates, primaryColor);
  const hasCustomColor = !!badgeStyle.bg;
  const templateIcon = template?.icon || '';

  // SLA check — only when showSLA is true and SLA fields exist
  const hasSLA = showSLA && (item.sla_resolution_deadline || item.sla_first_response_deadline);
  const isSLABreached = Boolean(item.sla_resolution_breached || item.sla_first_response_breached);
  const rowAccentColor = isSLABreached
    ? '#ef4444'
    : item.priority && ['urgent', 'critical'].includes(item.priority.toLowerCase())
      ? '#f97316'
      : getWorkItemTypeIdentity(item.item_type, primaryColor).color;

  // Resolve the click handler
  const handleClick = () => {
    onFocusChange(item.id);
    onViewItem?.(item);
  };

  // ---- Mobile layout ----
  if (isMobile) {
    return (
      <div className="select-none" role="treeitem" aria-selected={isFocused} aria-expanded={canExpand ? isExpanded : undefined} aria-level={depth + 1} aria-setsize={1} aria-posinset={flatIndex + 1}>
        <WorkItemHoverCard
          as="div"
          workItem={item}
          ref={rowRef}
          tabIndex={isFocused ? 0 : -1}
          className={`group flex items-start gap-2 py-3 px-3 transition-colors touch-manipulation
            hover:bg-gray-50 dark:hover:bg-gray-800/60 active:bg-gray-100 dark:active:bg-gray-600
            ${isFocused ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
          style={{
            paddingLeft: `${indentWidth + 8}px`,
            boxShadow: `inset 3px 0 0 ${rowAccentColor}`,
          }}
          onClick={handleClick}
          onFocus={() => onFocusChange(item.id)}
          onKeyDown={handleKeyDown}
        >
          {/* Expand/Collapse */}
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center -ml-1">
            {canExpand ? (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleExpand(item.id); }}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 active:bg-gray-300 dark:active:bg-gray-500 rounded transition-colors touch-manipulation"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                )}
              </button>
            ) : (
              <div className="w-5 h-5" />
            )}
          </div>

          {/* Status Icon */}
          <div className="flex-shrink-0 mt-0.5">
            {getStatusIcon(item.status, item.item_type)}
          </div>

          {/* Item Info — stacked on mobile */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-gray-500 dark:text-gray-300">
                {item.item_number}
              </span>
              {hasCustomColor ? (
                <span className="px-1.5 py-0.5 text-xs font-medium rounded border" style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.text, borderColor: badgeStyle.border }}>
                  {templateIcon ? `${templateIcon} ` : ''}{item.item_subtype || item.item_type}
                </span>
              ) : (
                <span className="px-1.5 py-0.5 text-xs font-medium rounded border" style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.text, borderColor: badgeStyle.border }}>
                  {templateIcon ? `${templateIcon} ` : ''}{item.item_subtype || item.item_type}
                </span>
              )}
              {item.priority && (
                <div className={`w-2 h-2 rounded-full ${getPriorityColor(item.priority)}`} title={`Priority: ${item.priority}`} />
              )}
            </div>
            <div className="mt-1 text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
              {item.title}
            </div>
            {/* Contact */}
            {showContact && item.contact_name && (
              <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-300">
                <User className="h-3 w-3" />
                <span className="truncate">{item.contact_name}</span>
              </div>
            )}
            {/* Progress + Status */}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {showProgress && item.progress_percentage !== undefined && item.progress_percentage > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600" style={{ width: `${item.progress_percentage}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-300">
                    {item.progress_percentage}%
                  </span>
                </div>
              )}
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(item.status, item.item_type)}`}>
                {formatAppointmentDisplayStatusLabel(item.status || '') || item.status}
              </span>
              {/* SLA Badge (mobile — compact variant) */}
              {hasSLA && item.sla_resolution_deadline && (
                <SLATimer
                  deadline={item.sla_resolution_deadline}
                  breached={!!item.sla_resolution_breached}
                  breachedAt={item.sla_resolution_breached_at}
                  variant="badge"
                  label="SLA"
                />
              )}
            </div>
            {/* Assignee Avatars */}
            {showAssignees && item.assignees && item.assignees.length > 0 && (
              <div className="mt-2">
                <AssigneeAvatars item={item} onOpenTeamMember={onOpenTeamMember} maxVisible={2} />
              </div>
            )}
          </div>

          {/* Mobile chevron */}
          <button
            onClick={(e) => { e.stopPropagation(); handleClick(); }}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 active:bg-gray-300 dark:active:bg-gray-500 rounded transition-colors touch-manipulation"
            aria-label="View details"
          >
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </button>
        </WorkItemHoverCard>

        {/* Children (recursive, respecting maxDepth) */}
        {canExpand && isExpanded && (
          <div role="group" className="divide-y divide-gray-200 dark:divide-gray-700">
            {item.children!.map((child, idx) => (
              <TreeNode
                key={child.id}
                item={child}
                depth={depth + 1}
                maxDepth={maxDepth}
                onViewItem={onViewItem}
                onAddChild={onAddChild}
                onEditItem={onEditItem}
                onDeleteItem={onDeleteItem}
                onOpenTeamMember={onOpenTeamMember}
                showProgress={showProgress}
                showMetrics={showMetrics}
                showAssignees={showAssignees}
                showContact={showContact}
                showSLA={showSLA}
                expandedMap={expandedMap}
                onToggleExpand={onToggleExpand}
                isMobile={isMobile}
                templates={templates}
                focusedId={focusedId}
                onFocusChange={onFocusChange}
                onNavigate={onNavigate}
                flatIndex={flatIndex + idx + 1}
                primaryColor={primaryColor}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- Desktop layout ----
  return (
    <div className="select-none" role="treeitem" aria-selected={isFocused} aria-expanded={canExpand ? isExpanded : undefined} aria-level={depth + 1} aria-setsize={1} aria-posinset={flatIndex + 1}>
      {/* Node Row — columnar layout matching DataGrid */}
      <WorkItemHoverCard
        as="div"
        workItem={item}
        ref={rowRef}
        tabIndex={isFocused ? 0 : -1}
        className={`group flex items-center px-3 py-2 transition-colors cursor-pointer
          hover:bg-gray-50 dark:hover:bg-gray-800/60
          ${isFocused ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/50 dark:bg-blue-900/20' : ''}`}
        style={{ boxShadow: `inset 3px 0 0 ${rowAccentColor}` }}
        onKeyDown={handleKeyDown}
        onFocus={() => onFocusChange(item.id)}
        onClick={handleClick}
      >
        {/* ITEM column (flex-1) — indent, chevron, status, number, title */}
        <div className="flex-1 flex items-center gap-2 min-w-0">
          {/* Indent spacer */}
          {depth > 0 && <div className="flex-shrink-0" style={{ width: `${indentWidth}px` }} />}

          {/* Expand/Collapse */}
          <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
            {canExpand ? (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleExpand(item.id); }}
                className="hover:bg-gray-200 dark:hover:bg-gray-600 rounded p-0.5 transition-colors"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                )}
              </button>
            ) : (
              <div className="w-4 h-4" />
            )}
          </div>

          {/* Status Icon */}
          <div className="flex-shrink-0">
            {getStatusIcon(item.status, item.item_type)}
          </div>

          {/* Item Number */}
          <span className="flex-shrink-0 text-[11px] font-mono text-gray-500 dark:text-gray-300">
            {item.item_number}
          </span>

          {/* Title */}
          <span className="flex-1 truncate text-[13px] font-semibold text-gray-900 dark:text-white">
            {item.title}
          </span>

          {/* Priority Dot — inline with title */}
          {item.priority && (
            <div
              className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${getPriorityColor(item.priority)}`}
              title={`Priority: ${item.priority}`}
            />
          )}
        </div>

        {/* TYPE column */}
        {showType && (
          <div className="w-24 flex-shrink-0 flex justify-center px-1">
            {hasCustomColor ? (
              <span
                className="px-2 py-0.5 text-[11px] font-medium rounded border whitespace-nowrap"
                style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.text, borderColor: badgeStyle.border }}
              >
                {templateIcon ? `${templateIcon} ` : ''}{item.item_subtype || item.item_type}
              </span>
            ) : (
              <span className="px-2 py-0.5 text-[11px] font-medium rounded border whitespace-nowrap" style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.text, borderColor: badgeStyle.border }}>
                {templateIcon ? `${templateIcon} ` : ''}{item.item_subtype || item.item_type}
              </span>
            )}
          </div>
        )}

        {/* ASSIGNED column */}
        {showAssignees && (
          <div className="w-24 flex-shrink-0 flex justify-center px-1">
            {item.assignees && item.assignees.length > 0 ? (
              <AssigneeAvatars item={item} onOpenTeamMember={onOpenTeamMember} maxVisible={2} />
            ) : (
              <span className="text-[11px] text-gray-400 italic">Unassigned</span>
            )}
          </div>
        )}

        {/* PROGRESS column — progress bar/percentage or hours */}
        {(showProgress || showMetrics) && (
          <div className="w-28 flex-shrink-0 flex items-center justify-center gap-1 px-1">
            {showProgress && item.progress_percentage !== undefined && item.progress_percentage > 0 ? (
              <>
                <div className="w-14 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${item.progress_percentage}%` }} />
                </div>
                <span className="text-[11px] text-gray-500 dark:text-gray-300">{item.progress_percentage}%</span>
              </>
            ) : showMetrics && item.estimated_hours !== undefined && item.estimated_hours !== null ? (
              <span className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-300">
                <Clock className="h-3 w-3" />
                {item.actual_hours || 0}/{item.estimated_hours}h
              </span>
            ) : (
              <span className="text-[11px] text-gray-400">-</span>
            )}
          </div>
        )}

        {/* STATUS column */}
        {showStatus && (
          <div className="w-24 flex-shrink-0 flex justify-center px-1">
            <span className={`px-2 py-0.5 text-[11px] font-medium rounded whitespace-nowrap ${getStatusColor(item.status, item.item_type)}`}>
              {formatAppointmentDisplayStatusLabel(item.status || '') || item.status}
            </span>
          </div>
        )}

        {/* ACTIONS column */}
        {showActions && (
          <div className="w-20 flex-shrink-0 flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 relative">
            {onAddChild && (
              <QuickAddTaskButton
                referenceType="work_item"
                referenceId={item.id}
                title={`Sub-item of ${item.item_number}`}
                variant="icon"
                size="sm"
                onTaskAdded={() => onAddChild(item)}
              />
            )}
            {onViewItem && (
              <button
                onClick={(e) => { e.stopPropagation(); onViewItem(item); }}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                title="View details"
              >
                <Eye className="h-3.5 w-3.5 text-gray-500 dark:text-gray-300" />
              </button>
            )}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                aria-label="More actions"
                aria-haspopup="true"
                aria-expanded={menuOpen}
              >
                <MoreVertical className="h-3.5 w-3.5 text-gray-500 dark:text-gray-300" />
              </button>
              {menuOpen && (
                <ContextMenu
                  item={item}
                  onViewItem={onViewItem}
                  onEditItem={onEditItem}
                  onDeleteItem={onDeleteItem}
                  onAddChild={onAddChild}
                  onClose={() => setMenuOpen(false)}
                />
              )}
            </div>
          </div>
        )}
      </WorkItemHoverCard>

      {/* Children (recursive, respecting maxDepth) */}
      {canExpand && isExpanded && (
        <div role="group" className="divide-y divide-gray-200 dark:divide-gray-700">
          {item.children!.map((child, idx) => (
            <TreeNode
              key={child.id}
              item={child}
              depth={depth + 1}
              maxDepth={maxDepth}
              onViewItem={onViewItem}
              onAddChild={onAddChild}
              onEditItem={onEditItem}
              onDeleteItem={onDeleteItem}
              onOpenTeamMember={onOpenTeamMember}
              showProgress={showProgress}
              showMetrics={showMetrics}
              showAssignees={showAssignees}
              showContact={showContact}
              showSLA={showSLA}
              showType={showType}
              showStatus={showStatus}
              showActions={showActions}
              expandedMap={expandedMap}
              onToggleExpand={onToggleExpand}
              templates={templates}
              focusedId={focusedId}
              onFocusChange={onFocusChange}
              onNavigate={onNavigate}
              flatIndex={flatIndex + idx + 1}
              primaryColor={primaryColor}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// FLAT ID LIST BUILDER (for keyboard navigation)
// ============================================================================

function buildFlatIdList(items: TreeWorkItem[], expandedMap: Map<string, boolean>, maxDepth: number, depth: number = 0): string[] {
  const result: string[] = [];
  for (const item of items) {
    result.push(item.id);
    if (item.children && item.children.length > 0 && depth < maxDepth) {
      const isExpanded = expandedMap.get(item.id) !== false; // default expanded
      if (isExpanded) {
        result.push(...buildFlatIdList(item.children, expandedMap, maxDepth, depth + 1));
      }
    }
  }
  return result;
}

function countTreeNodes(items: TreeWorkItem[]): number {
  return items.reduce((total, item) => total + 1 + countTreeNodes(item.children || []), 0);
}

function getExpandableItemIds(items: TreeWorkItem[], maxDepth: number, depth: number = 0): string[] {
  return items.flatMap((item) => {
    const childIds = item.children && item.children.length > 0 && depth < maxDepth
      ? getExpandableItemIds(item.children, maxDepth, depth + 1)
      : [];

    if (item.children && item.children.length > 0 && depth < maxDepth) {
      return [item.id, ...childIds];
    }

    return childIds;
  });
}

// ============================================================================
// MAIN TREE COMPONENT
// ============================================================================

const WorkItemTree: React.FC<WorkItemTreeProps> = ({
  items,
  onViewItem,
  onItemClick, // backward compat
  onAddChild,
  onEditItem,
  onDeleteItem,
  onOpenTeamMember,
  maxDepth = 10,
  showProgress = true,
  showMetrics = true,
  showAssignees = true,
  showContact = false,
  showSLA = false,
  showType = true,
  showStatus = true,
  showActions = true,
  loading = false,
  templates,
  stylePreset,
  className = '',
  activeItemType,
  activeItemTypeLabel,
  primaryColor = '#3b82f6',
  headerActions,
  onOpenBoardPreferences,
}) => {
  const { isMobile } = useResponsive();
  const presetViews = getKanbanStylePreset(stylePreset).views;
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [expandedMap, setExpandedMap] = useState<Map<string, boolean>>(new Map());
  const activeTypeIdentity = useMemo(
    () => getWorkItemTypeIdentity(activeItemType, primaryColor),
    [activeItemType, primaryColor]
  );
  const activeTypeAccent = useMemo(
    () => getWorkItemTypeAccent(activeItemType, primaryColor),
    [activeItemType, primaryColor]
  );
  const resolvedActiveTypeLabel = activeItemTypeLabel || activeTypeIdentity.label;

  // Resolve unified click handler
  const resolvedOnViewItem = onViewItem || onItemClick;

  // Build tree structure from flat list (if items have parent_id but aren't pre-nested)
  const treeData = useMemo(() => {
    // If items already have children arrays populated (from useWorkItemHierarchy), use as-is
    const hasPrebuiltChildren = items.some(item => item.children && item.children.length > 0);
    if (hasPrebuiltChildren) return items;

    // Otherwise build tree from flat list using parent_id
    const itemMap = new Map<string, TreeWorkItem & { children: TreeWorkItem[] }>();
    const rootItems: (TreeWorkItem & { children: TreeWorkItem[] })[] = [];

    items.forEach(item => {
      itemMap.set(item.id, { ...item, children: [] });
    });

    items.forEach(item => {
      const itemWithChildren = itemMap.get(item.id)!;
      if (item.parent_id) {
        const parent = itemMap.get(item.parent_id);
        if (parent) {
          parent.children.push(itemWithChildren);
        } else {
          rootItems.push(itemWithChildren);
        }
      } else {
        rootItems.push(itemWithChildren);
      }
    });

    return rootItems;
  }, [items]);

  // Flat ID list for keyboard navigation
  const flatIds = useMemo(() => {
    return buildFlatIdList(treeData, expandedMap, maxDepth);
  }, [treeData, expandedMap, maxDepth]);

  const expandableItemIds = useMemo(() => getExpandableItemIds(treeData, maxDepth), [treeData, maxDepth]);
  const totalNodeCount = useMemo(() => countTreeNodes(treeData), [treeData]);
  const nestedNodeCount = useMemo(() => Math.max(0, totalNodeCount - treeData.length), [totalNodeCount, treeData.length]);
  const isFlatStructure = expandableItemIds.length === 0 && nestedNodeCount === 0;
  const collapsedCount = useMemo(
    () => expandableItemIds.filter((id) => expandedMap.get(id) === false).length,
    [expandableItemIds, expandedMap]
  );

  const toggleExpand = useCallback((itemId: string) => {
    setExpandedMap((previous) => {
      const next = new Map(previous);
      const currentlyExpanded = previous.get(itemId) !== false;

      if (currentlyExpanded) {
        next.set(itemId, false);
      } else {
        next.delete(itemId);
      }

      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedMap(new Map());
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedMap(new Map(expandableItemIds.map((id) => [id, false])));
  }, [expandableItemIds]);

  // Keyboard navigation handler
  const handleNavigate = useCallback((direction: 'up' | 'down' | 'left' | 'right', currentId: string) => {
    const currentIndex = flatIds.indexOf(currentId);
    if (currentIndex === -1) return;

    if (direction === 'down' && currentIndex < flatIds.length - 1) {
      setFocusedId(flatIds[currentIndex + 1]);
    } else if (direction === 'up' && currentIndex > 0) {
      setFocusedId(flatIds[currentIndex - 1]);
    } else if (direction === 'left') {
      // Try to find parent in visible items
      const findParent = (nodeItems: TreeWorkItem[], targetId: string, parentId: string | null): string | null => {
        for (const nodeItem of nodeItems) {
          if (nodeItem.id === targetId) return parentId;
          if (nodeItem.children) {
            const found = findParent(nodeItem.children, targetId, nodeItem.id);
            if (found !== null) return found;
          }
        }
        return null;
      };
      const parentId = findParent(treeData, currentId, null);
      if (parentId) setFocusedId(parentId);
    }
    // 'right' is handled by TreeNode (expand or move to first child)
  }, [flatIds, treeData]);

  // Loading state
  if (loading) {
    return (
      <div
        className={`overflow-hidden rounded-xl border ${className}`}
        style={{
          borderColor: activeTypeAccent.border,
          background: `linear-gradient(180deg, ${activeTypeAccent.tint} 0%, rgba(255,255,255,0) 28%)`,
        }}
      >
        <div className={`border-b px-4 py-3 ${presetViews.headerBg}`} style={getWorkItemsAccentPanelStyle(activeTypeAccent, 'medium')}>
          <div className="flex items-center gap-2">
            {activeItemType && (
              <WorkItemsTypeAccentBadge label={resolvedActiveTypeLabel} accent={activeTypeAccent} />
            )}
            <div className="h-7 w-24 animate-pulse rounded-full bg-white/30 dark:bg-white/10" />
            <div className="h-7 w-20 animate-pulse rounded-full bg-white/20 dark:bg-white/5" />
          </div>
        </div>
        <div className="p-3 sm:p-4">
          <TreeSkeleton />
        </div>
      </div>
    );
  }

  // Empty state
  if (items.length === 0) {
    return (
      <div
        className={`flex min-h-[24rem] items-center justify-center overflow-hidden rounded-xl border ${className}`}
        style={{
          borderColor: activeTypeAccent.border,
          background: `linear-gradient(180deg, ${activeTypeAccent.tint} 0%, rgba(255,255,255,0) 28%)`,
        }}
      >
        <div className="max-w-sm px-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border" style={{ borderColor: activeTypeAccent.border, backgroundColor: activeTypeAccent.tintStrong }}>
            <FolderTree className="h-7 w-7" style={{ color: activeTypeAccent.color }} />
          </div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">No items to display</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Create a work item to see hierarchy, nesting, and child relationships here.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`overflow-hidden rounded-xl border ${className}`}
      style={{
        borderColor: activeTypeAccent.border,
        background: `linear-gradient(180deg, ${activeTypeAccent.tint} 0%, rgba(255,255,255,0) 20%)`,
      }}
    >
      <div className={`border-b px-3 py-2.5 sm:px-4 sm:py-3 ${presetViews.headerBg}`} style={getWorkItemsAccentPanelStyle(activeTypeAccent, 'medium')}>
        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {activeItemType && (
              <WorkItemsTypeAccentBadge label={resolvedActiveTypeLabel} accent={activeTypeAccent} />
            )}
            <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium ${presetViews.buttonBg} ${presetViews.buttonText}`} style={{ borderColor: activeTypeAccent.border }}>
              <span>{totalNodeCount}</span>
              <span className="opacity-70">items</span>
            </span>
            {nestedNodeCount > 0 ? (
              <>
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium ${presetViews.buttonBg} ${presetViews.buttonText}`} style={{ borderColor: activeTypeAccent.border }}>
                  <span>{treeData.length}</span>
                  <span className="opacity-70">roots</span>
                </span>
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium ${presetViews.buttonBg} ${presetViews.buttonText}`} style={{ borderColor: activeTypeAccent.border }}>
                  <span>{nestedNodeCount}</span>
                  <span className="opacity-70">nested</span>
                </span>
              </>
            ) : (
              <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium ${presetViews.buttonBg} ${presetViews.buttonText}`} style={{ borderColor: activeTypeAccent.border }}>
                <span>Flat</span>
                <span className="opacity-70">structure</span>
              </span>
            )}
            {expandableItemIds.length > 0 && (
              <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium ${presetViews.buttonBg} ${presetViews.buttonText}`} style={{ borderColor: activeTypeAccent.border }}>
                <span>{collapsedCount}</span>
                <span className="opacity-70">collapsed</span>
              </span>
            )}
            {showSLA && (
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-700 dark:border-amber-500/30 dark:bg-amber-900/20 dark:text-amber-300">SLA tracking</span>
            )}
          </div>

          {onOpenBoardPreferences && (
            <button
              type="button"
              onClick={onOpenBoardPreferences}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <Settings2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Preferences</span>
            </button>
          )}
          {headerActions && (
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2 justify-start lg:justify-end">
              {headerActions}
            </div>
          )}
        </div>

        {expandableItemIds.length > 0 && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={expandAll}
              className="rounded-full border px-3 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:bg-white/70 dark:text-gray-200 dark:hover:bg-white/10"
              style={{ borderColor: activeTypeAccent.border }}
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="rounded-full border px-3 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:bg-white/70 dark:text-gray-200 dark:hover:bg-white/10"
              style={{ borderColor: activeTypeAccent.border }}
            >
              Collapse all
            </button>
            <span className={`text-[11px] ${presetViews.mutedText}`}>Use arrow keys to move through the hierarchy and expand branches inline.</span>
          </div>
        )}

        {isFlatStructure && (
          <div className={`mt-2.5 flex flex-wrap items-center gap-2 text-[11px] ${presetViews.mutedText}`}>
            <span className="rounded-full border border-dashed px-3 py-1" style={{ borderColor: activeTypeAccent.border }}>
              No child branches in the current result set
            </span>
            <span>Hierarchy will appear automatically when parent-child links are present.</span>
          </div>
        )}
      </div>

      {/* Column headers — desktop only */}
      {!isMobile && (
        <div className={`flex items-center px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${presetViews.headerBg} ${presetViews.mutedText}`}>
          <div className="flex-1 pl-8">Item</div>
          {showType && <div className="w-24 flex-shrink-0 text-center">Type</div>}
          {showAssignees && <div className="w-24 flex-shrink-0 text-center">Assigned</div>}
          {(showProgress || showMetrics) && <div className="w-28 flex-shrink-0 text-center">Progress</div>}
          {showStatus && <div className="w-24 flex-shrink-0 text-center">Status</div>}
          {showActions && <div className="w-20 flex-shrink-0 flex justify-end">Actions</div>}
        </div>
      )}

      <div
        role="tree"
        aria-label="Work item hierarchy"
        className="divide-y divide-gray-200 dark:divide-gray-700"
        onFocus={() => {
          if (!focusedId && treeData.length > 0) {
            setFocusedId(treeData[0].id);
          }
        }}
      >
        {treeData.map((item, idx) => (
          <TreeNode
            key={item.id}
            item={item}
            depth={0}
            maxDepth={maxDepth}
            onViewItem={resolvedOnViewItem}
            onAddChild={onAddChild}
            onEditItem={onEditItem}
            onDeleteItem={onDeleteItem}
            onOpenTeamMember={onOpenTeamMember}
            showProgress={showProgress}
            showMetrics={!isMobile && showMetrics}
            showAssignees={showAssignees}
            showContact={showContact}
            showSLA={showSLA}
            showType={showType}
            showStatus={showStatus}
            showActions={showActions}
            expandedMap={expandedMap}
            onToggleExpand={toggleExpand}
            isMobile={isMobile}
            templates={templates}
            focusedId={focusedId}
            onFocusChange={setFocusedId}
            onNavigate={handleNavigate}
            flatIndex={idx}
            primaryColor={primaryColor}
          />
        ))}
      </div>
    </div>
  );
};

export default WorkItemTree;
